import { Controller } from "@hotwired/stimulus"

// Only POST forms; exclude inline auto-submit forms, remote/modal forms and explicitly opted-out forms.
const FORM_SELECTOR =
  'form[method="post"]:not([data-remote]):not(.remote-modal):not(.no-turbolinks):not(.inline):not([data-unsaved-changes-skip])'

// Connects to data-controller="panel--unsaved-changes" (attached to the panel layout body).
// Warns before leaving a page with unsaved form changes, both on in-app Turbolinks
// navigation (turbolinks:before-visit) and on tab close / reload (beforeunload).
//
// A form counts as dirty only when ALL of the following hold, so that plugins
// (select2, autonumeric, TinyMCE) rewriting values on init/focus do not cause false warnings:
// 1. the user started interacting with it — the snapshot is taken lazily on the first
//    pointerdown/keydown/focusin inside the form (before the value changes),
//    long after plugins have normalized the initial values,
// 2. an actual input/change event happened in it,
// 3. its current serialized state differs from the snapshot.
export default class extends Controller {
  static values = { message: String }

  connect() {
    this.snapshots = new WeakMap()
    this.interactedForms = new WeakSet()
    this.historyTrapArmed = false
    this.leavingViaTrap = false

    this.interactionStartHandler = (event) => this.snapshotIfNew(event)
    this.interactionHandler = (event) => this.markInteracted(event)
    this.submitHandler = (event) => this.markSubmitted(event)
    this.beforeVisitHandler = (event) => this.handleBeforeVisit(event)
    this.beforeUnloadHandler = (event) => this.handleBeforeUnload(event)
    this.popStateHandler = (event) => this.handlePopState(event)

    document.addEventListener("pointerdown", this.interactionStartHandler)
    document.addEventListener("keydown", this.interactionStartHandler)
    document.addEventListener("focusin", this.interactionStartHandler)
    document.addEventListener("tinymce:init", this.interactionStartHandler)
    document.addEventListener("turbolinks:before-visit", this.beforeVisitHandler)
    window.addEventListener("beforeunload", this.beforeUnloadHandler)
    window.addEventListener("popstate", this.popStateHandler)
    document.addEventListener("turbo:submit-start", this.submitHandler)

    window.$(document).on("input.unsaved-changes change.unsaved-changes", FORM_SELECTOR, this.interactionHandler)
    window.$(document).on("submit.unsaved-changes", FORM_SELECTOR, this.submitHandler)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.interactionStartHandler)
    document.removeEventListener("keydown", this.interactionStartHandler)
    document.removeEventListener("focusin", this.interactionStartHandler)
    document.removeEventListener("tinymce:init", this.interactionStartHandler)
    document.removeEventListener("turbolinks:before-visit", this.beforeVisitHandler)
    window.removeEventListener("beforeunload", this.beforeUnloadHandler)
    window.removeEventListener("popstate", this.popStateHandler)
    document.removeEventListener("turbo:submit-start", this.submitHandler)
    window.$(document).off(".unsaved-changes")
  }

  handleBeforeVisit(event) {
    if (this.anyFormDirty() && !window.confirm(this.messageValue)) {
      event.preventDefault()
    }
  }

  handlePopState() {
    if (this.leavingViaTrap) {
      this.leavingViaTrap = false
      return
    }

    if (!this.historyTrapArmed) return

    if (this.anyFormDirty() && !window.confirm(this.messageValue)) {
      this.nativeHistory("pushState", this.guardState, this.guardHref)
      return
    }

    this.leavingViaTrap = true
    window.history.back()
  }

  armHistoryTrap() {
    if (this.historyTrapArmed) return

    this.historyTrapArmed = true
    this.guardHref = window.location.href
    this.guardState = window.history.state || { turbolinks: {} }

    this.nativeHistory("replaceState", null, this.guardHref)
    this.nativeHistory("pushState", this.guardState, this.guardHref)
  }

  nativeHistory(method, state, url) {
    window.History.prototype[method].call(window.history, state, "", url)
  }

  handleBeforeUnload(event) {
    if (this.anyFormDirty()) {
      event.preventDefault()

      event.returnValue = this.messageValue
    }
  }

  snapshotIfNew(event) {
    const form = this.closestEligibleForm(event.target)
    if (!form) return

    if (!this.snapshots.has(form)) {
      this.takeSnapshot(form)
    }
  }

  markInteracted(event) {
    const form = this.closestEligibleForm(event.target)
    if (!form) return

    this.interactedForms.add(form)

    if (this.anyFormDirty()) {
      this.armHistoryTrap()
    }
  }

  markSubmitted(event) {
    const form = this.closestEligibleForm(event.target)
    if (form) {
      this.snapshots.delete(form)
      this.interactedForms.delete(form)
    }
  }

  closestEligibleForm(target) {
    return target.closest?.(FORM_SELECTOR)
  }

  takeSnapshot(form) {
    this.snapshots.set(form, this.serialize(form))
  }

  serialize(form) {
    // TinyMCE syncs editor content to its <textarea> only on save; force it so edits are detected
    if (window.tinymce) {
      window.tinymce.triggerSave()
    }

    return window.$(form).serialize()
  }

  isFormDirty(form) {
    return (
      this.interactedForms.has(form) &&
      this.snapshots.has(form) &&
      this.snapshots.get(form) !== this.serialize(form)
    )
  }

  anyFormDirty() {
    return Array.from(this.element.querySelectorAll(FORM_SELECTOR)).some((form) => this.isFormDirty(form))
  }
}
